import { useCallback, useEffect, useState } from "react";
import type { PrototypePreviewParts, PrototypePreviewView, PrototypeWorkflowState } from "@yagni/shared";
import {
  PrototypeFrame,
  prototypeFrameUrl,
  type PrototypePoint,
} from "../components/PrototypeFrame.js";
import { captureUiError } from "../sentry.js";
import { defaultPreviewView, PreviewControls } from "./PreviewControls.js";
import { Button, Input, NativeSelect, Textarea } from "../ui/index.js";

export function PrototypeReview({
  prototypeId,
  title,
  revision,
  revisions,
  width,
  onStartFeedback,
  parts,
}: {
  prototypeId: string;
  title: string;
  revision: number;
  revisions: number[];
  width?: number;
  onStartFeedback?: () => void;
  /** The revision's parts, which the Today / Proposed, State and Choice controls are built from. */
  parts?: PrototypePreviewParts | null;
}) {
  const [view, setView] = useState<PrototypePreviewView>(() => defaultPreviewView(parts));
  const partsKey = parts ? JSON.stringify(parts) : "";
  useEffect(() => { setView(defaultPreviewView(parts)); }, [revision, partsKey]);
  const [workflow, setWorkflow] = useState<PrototypeWorkflowState>({
    selectedRevision: null,
    comments: [],
    verifications: [],
    designSource: null,
  });
  const [compare, setCompare] = useState<number | null>(null);
  const [commentMode, setCommentMode] = useState(false);
  const [point, setPoint] = useState<PrototypePoint | null>(null);
  const [body, setBody] = useState("");
  const [stateDescription, setStateDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewUrl, setReviewUrl] = useState<string | null>(null);
  const base = `/api/prototypes/${encodeURIComponent(prototypeId)}`;
  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      const res = await fetch(`${base}/workflow`, {
        credentials: "include",
        signal,
      });
      if (!res.ok) throw new Error("Could not load prototype feedback.");
      const next = (await res.json()) as PrototypeWorkflowState;
      if (!signal?.aborted) setWorkflow(next);
    },
    [base],
  );
  useEffect(() => {
    const controller = new AbortController();
    const load = () => {
      if (document.visibilityState !== "hidden")
        void refresh(controller.signal).catch(() => {
          if (!controller.signal.aborted)
            setError("Could not refresh feedback. Try again.");
        });
    };
    load();
    const timer = setInterval(load, 5000);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [refresh]);
  useEffect(() => {
    setPoint(null);
    setBody("");
    setStateDescription("");
    setCommentMode(false);
    setReviewUrl(null);
  }, [prototypeId, revision]);
  const act = async (path: string, payload: unknown, method = "POST") => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${base}${path}`, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Could not save this change. Try again.");
      const result = (await res.json()) as { shareUrl?: string };
      if (path === "/review-links") setReviewUrl(result.shareUrl ?? null);
      if (path === "/share") setReviewUrl(null);
      if (path === "/comments") {
        setPoint(null);
        setBody("");
        setStateDescription("");
        setCommentMode(false);
      }
      await refresh();
    } catch {
      captureUiError(new Error("Prototype review action failed"), {
        area: "prototypes",
        operation: "review_action",
        severity: "error",
        expected: false,
      });
      setError("Could not save this change. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const comments = workflow.comments.filter(
    (comment) => comment.revision === revision,
  );
  const verifications = workflow.verifications.filter(
    (result) => result.revision === revision,
  );
  const inPlan = workflow.selectedRevision === revision;
  const frameLabel = (seq: number) => (
    <div className="font-mono text-[10.5px] uppercase tracking-wide text-[var(--text-tertiary)] px-1 pb-1.5">
      R{seq}
      {workflow.selectedRevision === seq ? " · in the plan" : ""}
    </div>
  );
  return (
    <section className="flex flex-col gap-3" aria-label="Prototype review">
      <div className="flex flex-wrap gap-2 items-center">
        {/* Already the plan's design: a fact, not a verb. */}
        <Button
          size="sm"
          variant={inPlan ? "secondary" : "primary"}
          disabled={busy || inPlan}
          onClick={() => void act("/selection", { revision })}
        >
          {inPlan ? "In the plan" : "Use this version in the plan"}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={commentMode}
          onClick={() => { onStartFeedback?.(); setCommentMode((value) => !value); }}
        >
          Point and comment
        </Button>
        <label className="ml-auto flex items-center gap-2 text-[12.5px] text-[var(--text-secondary)]">
          Compare revision
          <NativeSelect
            aria-label="Compare revision"
            selectSize="sm"
            wrapperClassName="w-[112px]"
            value={compare ?? ""}
            onChange={(event) =>
              setCompare(event.target.value ? Number(event.target.value) : null)
            }
          >
            <option value="">None</option>
            {revisions
              .filter((seq) => seq !== revision)
              .map((seq) => (
                <option key={seq} value={seq}>
                  R{seq}
                </option>
              ))}
          </NativeSelect>
        </label>
      </div>
      <PreviewControls parts={parts} value={view} onChange={setView} />
      {workflow.selectedRevision != null && (
        <p className="text-sm">
          R{workflow.selectedRevision} is selected for the plan. Changes after
          approval require a new plan review.
        </p>
      )}
      {workflow.designSource?.fallback && (
        <p className="text-sm text-[var(--text-secondary)]">
          Fallback styling. Repository design context was unavailable.
        </p>
      )}
      <div className={`grid gap-3 ${compare != null ? "lg:grid-cols-2" : ""}`}>
        <div
          className="material-glass p-2 overflow-hidden flex flex-col"
          style={{ height: "min(68vh, 640px)" }}
          data-testid="prototype-stage"
        >
          {compare != null && frameLabel(revision)}
          <PrototypeFrame
            src={prototypeFrameUrl(prototypeId, revision)}
            title={title}
            mode="full"
            width={width}
            commentMode={commentMode}
            onPoint={setPoint}
            view={view}
          />
        </div>
        {compare != null && (
          <div
            className="material-glass p-2 overflow-hidden flex flex-col"
            style={{ height: "min(68vh, 640px)" }}
          >
            {frameLabel(compare)}
            <PrototypeFrame
              src={prototypeFrameUrl(prototypeId, compare)}
              title={`${title}, comparison R${compare}`}
              mode="full"
              width={width}
              view={view}
            />
          </div>
        )}
      </div>
      {commentMode && (
        <p className="text-sm">
          Click a point in the preview, then describe your feedback below.
          Location hints are approximate.
        </p>
      )}
      {point && (
        <p className="text-sm">
          R{revision} · {point.viewportWidth} × {point.viewportHeight} · point{" "}
          {Math.round(point.x)}, {Math.round(point.y)} · scroll{" "}
          {Math.round(point.scrollX)}, {Math.round(point.scrollY)} ·{" "}
          {point.targetHint}
        </p>
      )}
      <form
        className="flex flex-col gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (body.trim())
            void act("/comments", {
              revision,
              body: body.trim(),
              ...(point ? { anchor: { ...point, stateDescription } } : {}),
            });
        }}
      >
        <label className="flex flex-col gap-1.5 text-[13px] text-[var(--text-primary)]">
          Feedback
          <Textarea
            value={body}
            maxLength={4000}
            rows={3}
            autosize
            placeholder="What should change in this revision?"
            onFocus={onStartFeedback}
            onChange={(event) => { onStartFeedback?.(); setBody(event.target.value); }}
          />
        </label>
        {point && (
          <label className="flex flex-col gap-1.5 text-[13px] text-[var(--text-primary)]">
            Interaction state
            <Input
              value={stateDescription}
              maxLength={1000}
              onChange={(event) => setStateDescription(event.target.value)}
              placeholder="For example: menu open after choosing a team"
            />
          </label>
        )}
        <div>
          <Button type="submit" size="sm" disabled={busy || !body.trim()}>
            Save comment
          </Button>
        </div>
      </form>
      <div className="material-print p-3">
        <h2 className="text-sm font-semibold">Feedback on R{revision}</h2>
        {comments.length === 0 && (
          <p className="text-sm">No comments on this revision.</p>
        )}
        {comments.map((comment) => (
          <article key={comment.id} className="py-2 border-b border-[var(--border-subtle)] last:border-b-0">
            <p className="whitespace-pre-wrap">{comment.body}</p>
            {comment.anchor && (
              <p className="text-sm text-[var(--text-secondary)]">
                Approximate location: {comment.anchor.targetHint} ·{" "}
                {comment.anchor.x}, {comment.anchor.y} in{" "}
                {comment.anchor.viewportWidth} × {comment.anchor.viewportHeight}
                ; scroll {comment.anchor.scrollX}, {comment.anchor.scrollY}.{" "}
                {comment.anchor.stateDescription} Recreate this state manually;
                point again to relocate.
              </p>
            )}
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() =>
                void act(
                  `/comments/${encodeURIComponent(comment.id)}`,
                  { resolved: !comment.resolved },
                  "PATCH",
                )
              }
            >
              {comment.resolved ? "Reopen comment" : "Resolve comment"}
            </Button>
          </article>
        ))}
      </div>
      <div className="material-print p-3">
        <h2 className="text-sm font-semibold">Implementation verification</h2>
        {verifications.length === 0 && (
          <p className="text-sm">
            Unverified. No browser verification has been recorded for R
            {revision}.
          </p>
        )}
        {verifications.map((result) => (
          <article key={result.id}>
            <p>
              {result.status}: {result.summary}
            </p>
            <ul>
              {result.checks.map((check, index) => (
                <li key={index}>
                  {check.name}: {check.status} {check.detail}
                </li>
              ))}
            </ul>
            {result.screenshots
              .filter(
                (shot) =>
                  /^https?:\/\//.test(shot.url) || /^\/(?!\/)/.test(shot.url),
              )
              .map((shot, index) => (
                <a
                  key={index}
                  href={shot.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--accent)] underline"
                >
                  {shot.label}
                </a>
              ))}
          </article>
        ))}
      </div>
      <div>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() => void act("/review-links", { revision })}
        >
          Create public review link for R{revision}
        </Button>
        <p className="text-sm">
          Anyone with this link can view this exact revision.
        </p>
        {reviewUrl && <p className="break-all text-sm">{reviewUrl}</p>}
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() => void act("/share", {}, "DELETE")}
        >
          Revoke all public links
        </Button>
      </div>
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
